/* eslint-disable max-lines-per-function, max-lines */
import { EventBus, Events } from "jiffy-store";
import { api } from "jiffy-api";
import useSvg from "common/utils/ui/use_svg";
import { isMobile } from "common/utils/browser";
import { track } from "jiffy-analytics";
import imageUrlProvider from "components/jiffy/product-details/image-url-provider";
import initImageZoom from "components/jiffy/product-details/image-zoom";
import initMockupTrigger from "components/jiffy/mockup-tool-modal/mockup-tool-modal";
import "./product-details-image-carousel.scss";
import { isCustomizableMadePage } from "components/jiffy/product-details/customizable-made-page-utils";
import restorePreview from "components/jiffy/design-upload-cta/design-upload-cta-pdp/restore-preview";

const SELECTORS = {
  CAROUSEL: ".product-details-image-carousel",
  DEFAULT_CAROUSEL: ".js-carousel-wrapper:not(.hidden)",
  THUMBNAILS: ".js-carousel-thumbnails",
  ACTIVE_THUMB: "product-details-image-carousel__thumb-image--active",
  CAROUSEL_ITEM: ".js-carousel-item",
  IMAGE: ".js-carousel-image",
  LENS: ".js-carousel-lens",
  PREVIEW_WRAPPER: ".js-preview-wrapper",
  PANEL: ".js-info-right",
  ACTIVE_CAROUSEL_ITEM:
    ".js-carousel-wrapper:not(.hidden) .js-carousel-item:not(.product-details-image-carousel__item--hidden)",
  PRODUCT_LIGHTBOX_TOGGLER: ".product-lightbox .product-lightbox__back-button"
};

const THUMB_MAX_COUNT = 6;

const $getMockupTrigger = () => document.querySelector(".js-mockup-trigger");
const $getActiveImage = () =>
  document.querySelector(".js-carousel-item:not(.product-details-image-carousel__item--hidden)");
const $getVideoPlayers = () => document.querySelectorAll(".js-carousel-item-video-player");
const $getCurrentItemVideoPlayer = $$currItem =>
  $$currItem[0]?.querySelector(".js-carousel-item-video-player");
const $getVideoPlayer = $wrapper => $wrapper.querySelector(".js-carousel-item-video-player");
let dtf = false;

const createCarouselImage = (item, index, printableAreaData = null) => {
  let mockupKeyString;
  let pictureId;
  if (item.mockup_enabled_for_position) {
    mockupKeyString = `data-mockup-key="${item.mockup_key}"`;
    pictureId = `data-picture-id="${item.id}"`;
  }
  const imageSrc = dtf
    ? imageUrlProvider.dtfImageUrl(item.original_url)
    : imageUrlProvider.imageUrl(item.original_url);

  // Add printable area data attributes to the first image only for Jiffy Made pages
  let printableAreaAttrs = "";
  if (index === 0 && printableAreaData && isCustomizableMadePage()) {
    if (printableAreaData.zoomed_front_picture) {
      printableAreaAttrs += ` data-front-zoomed-image-url="${printableAreaData.zoomed_front_picture}"`;
    }
    if (printableAreaData.printable_area_scaled) {
      printableAreaAttrs += ` data-printable-area='${JSON.stringify(printableAreaData.printable_area_scaled)}'`;
    }
  }

  return `<li class="js-carousel-item product-details-image-carousel__item
    ${index > 0 ? "product-details-image-carousel__item--hidden" : ""}"
    ${mockupKeyString}
    ${pictureId}
  >
    <div class="js-carousel-lens product-details-image-carousel__lens"></div>
    <a
      class="js-product-lightbox-link"
      data-img-src="${item.original_url}">
      <img
        class="js-carousel-image product-details-image-carousel-element lazyload"
        height="0"
        alt="${item.composition_name}"
        data-src="${imageSrc}"
        data-thumbnail-src="${imageUrlProvider.thumbUrl(item.original_url)}"
        data-zoom-src="${imageUrlProvider.zoomUrl(item.original_url, item.width, item.height)}"
        data-width="${item.width}"
        data-height="${item.height}"
        ${printableAreaAttrs}
        src="https://d1za1hgcop7m55.cloudfront.net/assets/FFFFFF-1-e0ee6ce31a24984036bfd39b55ea8d696734e1eaa40c30010cf12c63fd04e196.png">
    </a>
  </li>
  `;
};

const createCarouselVideo = (
  item,
  index
  // printableAreaData parameter is not used for videos but kept for consistency with createCarouselImage
) => `<li class="js-carousel-item product-details-image-carousel__item
    ${index > 0 ? "product-details-image-carousel__item--hidden" : ""}"
  >
    <video
      controls
      controlslist="nodownload"
      preload="metadata"
      muted
      data-id="${item.id}"
      data-thumbnail-src="${imageUrlProvider.thumbUrl(item.original_url)}"
      class="product-details-image-carousel__video js-carousel-item-video-player"
    >
      <source src="${item.src}">
    </video>
  </li>
  `;

const createCarouselItem = (item, index, printableAreaData = null) => {
  const createFn = item.type === "video" ? createCarouselVideo : createCarouselImage;

  return createFn(item, index, printableAreaData);
};

const createColorCarousel = color => {
  if (!color) return null;
  const {
    carousel,
    composition_name: compositionName,
    printable_area_data: printableAreaData
  } = color;

  return `
    <ul class="product-details-image-carousel__jiffy-carousel js-carousel-wrapper"
        data-color-name="${compositionName}"
    >
      ${carousel.map((item, index) => createCarouselItem(item, index, printableAreaData)).join("")}
    </ul>
  `;
};

const initVideoEvents = () => {
  $getVideoPlayers().forEach(player => {
    const { id, tracked } = player.dataset;

    if (tracked) return;

    player.addEventListener("play", () => track("pdp_video_started", { id }));
    player.addEventListener("pause", () => track("pdp_video_paused", { id }));
    player.dataset.tracked = true;
  });
};

const didMount = () => {
  const wrapperNode = document.querySelector(SELECTORS.DEFAULT_CAROUSEL);
  return wrapperNode instanceof HTMLElement;
};

export const productImageCarousel = {
  zoomListeners: [],
  thumbListeners: [],
  lastThumb: null,
  currentItem: 0,
  currentMediaType: null,
  selectedColor: null,
  latestDesignProject: null,
  showDefaultCarousel() {
    const $carousel = $(SELECTORS.CAROUSEL).find(SELECTORS.DEFAULT_CAROUSEL);
    if (this.lastThumb) {
      this.destroyThumb(this.lastThumb);
    }
    if ($carousel.length > 0) {
      this.setupThumbnails($carousel);
    }
  },
  changeColor(selectedColor) {
    this.pauseAllVideos();
    this.selectedColor = selectedColor;
    const container = document.querySelector(SELECTORS.CAROUSEL);
    if (!this.carousels) {
      container.classList.add("product-details-image-carousel--loading");
      return;
    }
    container.querySelector(SELECTORS.DEFAULT_CAROUSEL).classList.add("hidden");

    const curCarousel = container.querySelector(
      `:not(${SELECTORS.THUMBNAILS})[data-color-name="${selectedColor}"]`
    );

    if (curCarousel && !dtf) {
      curCarousel.classList.remove("hidden");
      return;
    }

    const newCarousel = this.carousels.find(item => item.composition_name === selectedColor);
    const carouselWrappers = container.querySelectorAll("ul");
    const lastWrapper = carouselWrappers[carouselWrappers.length - 1];

    lastWrapper.insertAdjacentHTML("afterend", createColorCarousel(newCarousel));

    initVideoEvents();
  },
  destroyThumb(thumb) {
    thumb.querySelectorAll("img").forEach(el => {
      const event = this.thumbListeners.shift();
      el.removeEventListener("click", event);
      el.removeEventListener("mousemove", event);
    });
  },
  goToIndex($carousel, index) {
    const $carouselItems = $carousel.children();
    const $currentItem = $carouselItems.eq(index);

    if (!$currentItem.hasClass("product-details-image-carousel__item--hidden")) {
      return;
    }

    this.pauseAllVideos();
    $carouselItems.addClass("product-details-image-carousel__item--hidden");
    this.displayMockupButton($currentItem[0]);
    $currentItem.removeClass("product-details-image-carousel__item--hidden");
    this.playCurrentVideo($currentItem);
  },
  pauseAllVideos() {
    $getVideoPlayers().forEach(player => player.pause());
  },
  playCurrentVideo($$currentItem) {
    const $currPlayer = $getCurrentItemVideoPlayer($$currentItem);
    if (!$currPlayer) return;

    $currPlayer.play();
  },
  handleThumbnailSelection(event, { el, $carousel, index }) {
    event?.stopPropagation();
    const container = document.querySelector(".product-details-image-carousel");

    if (container) {
      this.currentMediaType = el.dataset.type;
    }

    this.currentItem = index;
    this.goToIndex($carousel, index);

    const prevActive = el.parentNode.querySelector(`.${SELECTORS.ACTIVE_THUMB}`);
    if (prevActive) {
      prevActive.classList.remove(SELECTORS.ACTIVE_THUMB);
    }
    el.classList.add(SELECTORS.ACTIVE_THUMB);
  },
  setupThumbnails($carousel) {
    const carousel = document.querySelector(SELECTORS.DEFAULT_CAROUSEL);
    if (!carousel) return;
    this.currentItem = 0;

    const items = Array.from(carousel.querySelectorAll(SELECTORS.CAROUSEL_ITEM));
    const thumbs = document.querySelector(SELECTORS.THUMBNAILS);

    if (!thumbs) return;

    if (items.length < 1) {
      this.lastThumb = thumbs;
      thumbs.classList.add("product-details-image-carousel__thumbnails--empty");
      return;
    }
    thumbs.classList.remove("product-details-image-carousel__thumbnails--empty");

    // Init only on change color, don't touch loaded thumbnail
    if (this.lastThumb) {
      thumbs.textContent = "";

      const thumbnailItems = items.map(element => {
        const image = element.querySelector(SELECTORS.IMAGE);
        let item = null;
        if (image && image.dataset.src) {
          item = document.createElement("img");
          item.className =
            "product-details-image-carousel__thumb-image product-details-image-carousel-element";
          item.src = image.dataset.thumbnailSrc;
        } else if (image) {
          item = document.createElement("div");
          item.className =
            "product-details-image-carousel__thumb-image product-details-image-carousel__thumb-image--fit product-details-image-carousel-element";
          item.innerHTML = `
            ${useSvg("preview-play", { class: "product-details-image-carousel__thumb-icon" })}
            <img src="${image.dataset.thumbnailSrc}" />
          `;
        } else {
          item = document.createElement("div");
          item.className =
            "product-details-image-carousel__thumb-image product-details-image-carousel__thumb-image--fit product-details-image-carousel-element";
          item.innerHTML = `
            ${useSvg("preview-play", { class: "product-details-image-carousel__thumb-icon" })}
            <img src="${$getVideoPlayer(element).dataset.thumbnailSrc}" />
          `;
        }
        return item;
      });

      while (thumbnailItems.length < THUMB_MAX_COUNT) {
        const placeholder = document.createElement("div");
        placeholder.className = "product-details-image-carousel__thumb-placeholder";
        thumbnailItems.push(placeholder);
      }

      thumbnailItems.forEach(el => thumbs.appendChild(el));
    }
    this.lastThumb = thumbs;

    thumbs.querySelectorAll(".product-details-image-carousel-element").forEach((el, index) => {
      // Make first item active and remove it from others
      if (index === 0) {
        el.classList.add(SELECTORS.ACTIVE_THUMB);
      } else {
        el.classList.remove(SELECTORS.ACTIVE_THUMB);
      }
      const handleSelection = event =>
        this.handleThumbnailSelection(event, { el, $carousel, index });
      this.thumbListeners.push(handleSelection);
      ["click", "mousemove", "focus"].forEach(event => {
        el.addEventListener(event, e => {
          if (el?.classList.contains(SELECTORS.ACTIVE_THUMB)) return;
          handleSelection(e);
        });
      });
    });
  },
  bindImageListener() {
    const carouselImages = $("img.lazyload");
    const lazyloadedCallback = () => {
      this.refreshImages();
      carouselImages.off("lazyloaded", lazyloadedCallback);
    };

    carouselImages.on("lazyloaded", lazyloadedCallback);
  },
  refreshImages() {
    const $carousel = $(SELECTORS.DEFAULT_CAROUSEL);
    $carousel.scrollTop(0);
    $carousel.addClass("product-details-image-carousel__carousel-visible");
    const activeImage = Array.from($carousel[0].children).find(
      el => !el.classList.contains("product-details-image-carousel__item--hidden")
    );
    this.displayMockupButton(activeImage);
    this.updateMadePreview();
  },
  triggerUpdate() {
    this.bindImageListener();
    this.showDefaultCarousel();
    this.updateZoomListeners();
  },
  updateZoomListeners() {
    if (!isMobile()) {
      const $carousel = $(SELECTORS.CAROUSEL).find(SELECTORS.DEFAULT_CAROUSEL);
      this.zoomListeners.forEach(l => l()); // Remove zoom events
      if ($carousel.length) {
        this.zoomListeners = initImageZoom(SELECTORS);
      }
    }
  },
  async getDesignProjectImages() {
    const images = { front: null, back: null };

    try {
      const designProject =
        this.latestDesignProject || (await api.designLab.getLatestDesignProject());
      this.latestDesignProject = designProject;
      if (!designProject) return images;

      const { placements } = designProject;

      if (!placements || Object.keys(placements).length === 0) {
        return images;
      }

      if (placements.front && placements.front.dtf_artwork) {
        const frontPlacement = placements.front;
        const frontArtwork = frontPlacement.dtf_artwork;

        images.front = frontPlacement.remove_background
          ? frontArtwork.no_bg_url || frontArtwork.no_bg_no_trim_url
          : frontArtwork.with_bg_url || frontArtwork.original_image_url;
      }

      if (placements.back && placements.back.dtf_artwork) {
        const backPlacement = placements.back;
        const backArtwork = backPlacement.dtf_artwork;

        images.back = backPlacement.remove_background
          ? backArtwork.no_bg_url || backArtwork.no_bg_no_trim_url
          : backArtwork.with_bg_url || backArtwork.original_image_url;
      }

      return images;
    } catch (_error) {
      return images;
    }
  },
  async updateMadePreview() {
    const images = await this.getDesignProjectImages();

    await restorePreview(images.front);

    this.updateZoomListeners();
  },
  init() {
    if (!didMount()) return;

    EventBus.on(Events.PRODUCT_COLOR_SELECTED, (event, { color }) => {
      this.changeColor(color);
      this.triggerUpdate();
      this.refreshImages();
    });

    const fetchColorsOnce = () => {
      if (!this.isFetched) {
        this.isFetched = true;
        this.fetchColors();
      }
    };

    EventBus.on(Events.COLOR_PICKER_OPENED, fetchColorsOnce);
    EventBus.on(Events.PRODUCT_COLOR_SELECTED_FROM_SLIDER, fetchColorsOnce);
    EventBus.on(Events.SELECTED_VARIANT_CHANGED, ({ target }) => {
      dtf = true;
      this.selectedColor = "Transfer";
      const container = document.querySelector(SELECTORS.CAROUSEL);
      this.carousels = target.carouselData.product_images;
      this.preloadThumbnails(target.carouselData.product_images);
      this.changeColor(this.selectedColor);
      this.triggerUpdate();

      container.classList.remove("product-details-image-carousel--loading");
    });

    this.displayMockupButton($getActiveImage());
    this.triggerUpdate();
  },
  preloadThumbnails(colors) {
    colors.forEach(color =>
      color.carousel.forEach(item => {
        const img = new Image();
        img.src = imageUrlProvider.thumbUrl(item.original_url);
      })
    );
  },
  async fetchColors() {
    const container = document.querySelector(SELECTORS.CAROUSEL);
    const currentColor = container.querySelector(SELECTORS.DEFAULT_CAROUSEL);
    const { colorName } = currentColor.dataset;
    this.selectedColor = colorName;
    const { productId } = container.dataset;
    const params = {
      slug: productId,
      made_dedicated_pdp: isCustomizableMadePage()
    };
    const data = await api.jiffyshirts.carousel.getCarouselImages(params);
    this.carousels = data.product_images.filter(
      ({ composition_name: compositionName }) => compositionName !== colorName
    );
    this.preloadThumbnails(data.product_images);

    if (this.selectedColor !== colorName) {
      this.changeColor(this.selectedColor);
      this.triggerUpdate();
    }

    container.classList.remove("product-details-image-carousel--loading");
  },
  displayMockupButton(currentImage) {
    const { mockupKey, pictureId } = currentImage.dataset;
    const $mockupTrigger = $getMockupTrigger();
    if (!$mockupTrigger) return;

    if (mockupKey && mockupKey !== "undefined") {
      $mockupTrigger.setAttribute("data-hash", mockupKey);
      $mockupTrigger.setAttribute("data-picture-id", pictureId);
      $mockupTrigger.classList.remove("hidden");
    } else {
      $mockupTrigger.classList.add("hidden");
    }
  },
  selectItemByIndex(index) {
    return this.thumbListeners[index]?.();
  }
};

export default () => {
  productImageCarousel.init();
  initMockupTrigger();
  initVideoEvents();
};
